/**
 * Browsing twenty years of Friday nights.
 *
 * The list is filtered by what can actually be the length you asked for, because a
 * library that offers a recording and then cannot deliver it is worse than a shorter
 * library. Each row carries its date and its shape — the speech/silence band — so the
 * 12%-to-60% spread across the corpus is visible as texture rather than stated as a
 * number.
 */

import { useMemo, useState } from 'preact/hooks';
import type { Recording } from '../types';
import { manifest } from '../state/manifest';
import { isEligible } from '../session/eligibility';
import { isWithheld } from '../session/withheld';
import { seededRandom, shuffleAvoidingRecent } from '../session/select';
import { settings } from '../state/settings';
import { history, lastHeard } from '../state/history';

interface Props {
  onPick: (rec: Recording) => void;
  onBack: () => void;
}

type Sort = 'shuffle' | 'newest' | 'oldest' | 'quietest' | 'talkiest';

const SORT_LABEL: Record<Sort, string> = {
  shuffle: 'Shuffle',
  newest: 'Newest first',
  oldest: 'Oldest first',
  quietest: 'Most silence',
  talkiest: 'Most voice',
};

/** The speech/silence band for one recording, drawn straight from its gaps. */
function Shape({ rec }: { rec: Recording }) {
  const total = rec.nativeSec || 1;
  const marks = rec.speech.map((s) => ({
    left: (s.srcStart / total) * 100,
    width: Math.max(((s.srcEnd - s.srcStart) / total) * 100, 0.6),
  }));
  return (
    <div
      aria-hidden="true"
      style={{ position: 'relative', height: '10px', flex: 1,
               borderTop: '1px solid var(--rule)', marginTop: '5px' }}
    >
      {marks.map((m, i) => (
        <span key={i} style={{
          position: 'absolute', left: `${m.left}%`, width: `${m.width}%`,
          top: '-3px', height: '6px', background: 'var(--accent-fill)',
          borderRadius: '2px',
        }} />
      ))}
    </div>
  );
}

export function Library({ onPick, onBack }: Props) {
  const m = manifest.value;
  const s = settings.value;
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('shuffle');
  // The seed, not the order, is the state. Re-rolling it is what "shuffle again" means,
  // and holding it still is what stops the list rearranging itself as you type.
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 2 ** 31));
  const [onlyReachable, setOnlyReachable] = useState(true);
  const [themes, setThemes] = useState<string[]>([]);

  const rows = useMemo(() => {
    if (!m) return [];
    let out = m.recordings.filter((r) => !isWithheld(r.id));
    if (onlyReachable) out = out.filter((recording) => isEligible({
      recording, targetSec: s.lastDurationSec, cadence: s.cadence,
      desiredSpeechRatio: s.desiredSpeechRatio ?? undefined,
    }));
    if (themes.length) out = out.filter((r) => r.themes.some((t) => themes.includes(t)));
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter((r) =>
        r.title.toLowerCase().includes(needle)
        || r.recordedOn.includes(needle)
        || new Date(r.recordedOn).toLocaleDateString('en-GB',
          { day: 'numeric', month: 'long', year: 'numeric' }).toLowerCase().includes(needle)
        || r.themes.some((t) => t.toLowerCase().includes(needle)));
    }
    if (sort === 'shuffle') {
      // The same rule the session picker uses, so "shuffle" means one thing in this app:
      // random, except that what you heard in the last dozen sits sinks to the bottom.
      return shuffleAvoidingRecent(out, (r) => r.id, history.value, seededRandom(seed));
    }
    const cmp: Record<Exclude<Sort, 'shuffle'>, (a: Recording, b: Recording) => number> = {
      newest: (a, b) => b.recordedOn.localeCompare(a.recordedOn),
      oldest: (a, b) => a.recordedOn.localeCompare(b.recordedOn),
      quietest: (a, b) => a.speechRatio - b.speechRatio,
      talkiest: (a, b) => b.speechRatio - a.speechRatio,
    };
    return out.sort(cmp[sort]);
  }, [m, q, sort, seed, onlyReachable, themes, s.lastDurationSec, s.cadence, history.value]);

  return (
    <div class="screen">
      <div class="shell stack" style={{ flex: 1 }}>
        <button class="btn btn-quiet" onClick={onBack}
                style={{ alignSelf: 'flex-start', minHeight: '2.5rem', paddingLeft: 0 }}>
          ← Back
        </button>

        <h1>The library</h1>

        <div class="stack-sm">
          <input
            type="search"
            value={q}
            placeholder="A date, a year, a theme…"
            aria-label="Search the library"
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            style={{
              font: 'inherit', padding: '0.7rem 0.9rem', background: 'var(--raised)',
              color: 'var(--ink)', border: '1px solid var(--rule)',
              borderRadius: 'var(--radius)', boxShadow: 'inset 0 1px 0 var(--sheen)',
              width: '100%',
            }}
          />

          <div class="row" style={{ flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
            <button class="chip" aria-pressed={onlyReachable}
                    onClick={() => setOnlyReachable(!onlyReachable)}>
              Fits {Math.round(s.lastDurationSec / 60)} min
            </button>
            {(['shuffle', 'newest', 'oldest', 'quietest', 'talkiest'] as Sort[]).map((v) => (
              <button
                key={v}
                class="chip"
                aria-pressed={sort === v}
                onClick={() => {
                  // Tapping Shuffle always re-rolls, including when it is already on.
                  // Coming back to shuffle and getting the previous order is not shuffle.
                  if (v === 'shuffle') setSeed(Math.floor(Math.random() * 2 ** 31));
                  setSort(v);
                }}
              >
                {SORT_LABEL[v]}
                {v === 'shuffle' && sort === 'shuffle' && (
                  <span aria-hidden="true"
                        style={{ opacity: 0.7, marginLeft: '0.35em' }}>⟳</span>
                )}
              </button>
            ))}
          </div>

          {!!m?.themes.length && (
            <div class="row" style={{ flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
              {m.themes.map((t) => (
                <button
                  key={t.id}
                  class="chip"
                  aria-pressed={themes.includes(t.id)}
                  onClick={() => setThemes(themes.includes(t.id)
                    ? themes.filter((x) => x !== t.id)
                    : [...themes, t.id])}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <p class="dim" data-testid="library-count" style={{ fontSize: 'var(--step--1)' }}>
            <span class="fig">{rows.length}</span> of{' '}
            <span class="fig">{m?.recordings.length ?? 0}</span>
            {sort === 'shuffle' && ' · shuffled, recent sits last'}
          </p>
        </div>

        {rows.length === 0 && (
          <p class="dim">
            Nothing here matches. {onlyReachable
              ? 'Try turning off the length filter, or asking for a longer sit.'
              : 'Try a different search.'}
          </p>
        )}

        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} class="stack-sm">
          {rows.slice(0, 120).map((r) => {
            const heard = lastHeard(r.id);
            const days = heard ? Math.round((Date.now() - heard) / 86_400_000) : null;
            return (
              <li key={r.id}>
                <button
                  onClick={() => onPick(r)}
                  style={{ width: '100%', textAlign: 'left', padding: 'var(--gap-2) 0',
                           borderBottom: '1px solid var(--rule)' }}
                >
                  <div class="spread">
                    <span class="eyebrow">
                      {new Date(r.recordedOn).toLocaleDateString('en-GB',
                        { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span class="fig dim" style={{ fontSize: 'var(--step--1)' }}>
                      {Math.round(r.nativeSec / 60)} min · {Math.round(r.speechRatio * 100)}% voice
                    </span>
                  </div>
                  <div class="serif" style={{ fontSize: 'var(--step-1)' }}>{r.title}</div>
                  <Shape rec={r} />
                  {days !== null && (
                    <span class="dim" style={{ fontSize: 'var(--step--1)' }}>
                      Heard {days === 0 ? 'today' : `${days} days ago`}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
